import React, {
  createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import { Character } from '../../dorian-shared/types/character/Character';
import { useApiService } from '../ApiServiceContext/ApiServiceContext';

export type BookCharactersContextType = {
  bookCharacters: Character[] | null;
  fetchBookCharacters: (bookId: number) => Promise<void>;
};

export const BookCharactersContext = createContext<BookCharactersContextType>({
  bookCharacters: null,
  fetchBookCharacters: async () => undefined,
});

export function BookCharactersContextProvider({ children }: { children: ReactNode }) {
  const [bookCharacters, setBookCharacters] = useState<Character[] | null>(null);
  const loadingRef = useRef(false);
  const apiService = useApiService();

  const fetchBookCharacters = useCallback(async (bookId: number) => {
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    try {
      const result = await apiService.fetchCharactersByBookId(bookId);
      setBookCharacters(result ?? null);
    } catch {
      // Intentional: fetch failure leaves bookCharacters unchanged.
    } finally {
      loadingRef.current = false;
    }
  }, [apiService]);

  const value = useMemo(() => ({
    bookCharacters,
    fetchBookCharacters,
  }), [bookCharacters, fetchBookCharacters]);

  return (
    <BookCharactersContext.Provider value={value}>
      {children}
    </BookCharactersContext.Provider>
  );
}

export const useBookCharactersContext = () => useContext(BookCharactersContext);
